import Divider from 'components/divider/Divider';
import React, { FC } from 'react';
import { VoidFn } from 'types/baseTypes';
import { SelectionCircleBlueIcon, SelectionCircleIcon } from 'assets/icons';
import { FilterSelectionType } from 'constants/common';

interface DropdownPopoverPanelProps {
  data: Record<string, unknown>[];
  dataKey: string;
  selectedDraftArray: string[];
  setSelectedDraftArray: (selectedDraftArray: string[]) => void;
  setSelectedArray: (selectedArray: string[]) => void;
  searchText?: string;
  setSearchText?: (searchText: string) => void;
  dataIdKey: string;
  filterPlaceholderText: string;
  filterPlaceholderStyle?: string;
  searchPlaceHolderText?: string;
  searchInputStyle?: string;
  allowSearch: boolean;
  showResultsCount?: boolean;
  filterId: string;
  secondaryDataKey?: string;
  searchFilterId?: string;
  close: VoidFn;
  showCustomOptions?: boolean;
  customOption?: (option) => React.ReactNode;
  showSeparatorAfterFirstItem?: boolean;
  selectionType?: FilterSelectionType;
}

const DropdownPopoverPanel: FC<DropdownPopoverPanelProps> = ({
  data,
  dataKey,
  selectedDraftArray,
  setSelectedDraftArray,
  setSelectedArray,
  searchText = '',
  setSearchText = () => ({}),
  dataIdKey,
  filterPlaceholderText,
  filterPlaceholderStyle = '',
  searchPlaceHolderText = '',
  searchInputStyle = '',
  allowSearch,
  showResultsCount = true,
  filterId,
  secondaryDataKey = '',
  searchFilterId,
  close,
  showCustomOptions = false,
  customOption,
  showSeparatorAfterFirstItem = false,
  selectionType = FilterSelectionType.CHECKBOX
}) => {
  const applyFilter = () => {
    setSelectedArray([...selectedDraftArray]);
  };

  const handleChange = id => {
    if (selectionType === FilterSelectionType.RADIO) {
      setSelectedDraftArray([id]);
    } else {
      const isChecked = selectedDraftArray.includes(id);
      if (isChecked) {
        setSelectedDraftArray(selectedDraftArray.filter(customerId => customerId !== id));
      } else {
        setSelectedDraftArray([...selectedDraftArray, id]);
      }
    }
  };

  const filterData = () => {
    const tempFilterData = data.filter(_ => {
      const name = `${_[dataKey]} ${secondaryDataKey ? _[secondaryDataKey] : ''}`;
      return name.toLowerCase().includes(searchText.toLowerCase());
    });
    return tempFilterData;
  };

  const filteredData = filterData();

  const bulkSelectAction = () => {
    if (selectedDraftArray.length === filteredData.length) {
      setSelectedDraftArray([]);
    } else {
      setSelectedDraftArray(filteredData.map(_ => String(_[dataIdKey])));
    }
  };
  return (
    <>
      <div className={`px-3 text-sbase text-primaryText ${filterPlaceholderStyle}`}>{filterPlaceholderText}</div>
      {allowSearch && (
        <div className="px-3 pb-1 pt-2.5">
          <input
            type="text"
            id={searchFilterId}
            onChange={e => setSearchText(e.target.value)}
            className={`block h-8 w-full rounded-md border border-gray-1 px-2 py-1 text-sbase
                       text-gray-900 ${searchInputStyle}`}
            placeholder={searchPlaceHolderText}
            required
          />
        </div>
      )}
      {showResultsCount ? (
        <div className="flex justify-between px-3">
          <div className=" pb-4 pt-1 text-px13 text-primaryText">
            Showing {filteredData.length} of {filteredData.length}
          </div>
          {filteredData.length > 0 && selectionType === FilterSelectionType.CHECKBOX && (
            <div className="ml-4 cursor-pointer pb-4 pt-1 text-px13 text-secondary" onClick={bulkSelectAction}>
              {selectedDraftArray.length === filteredData.length ? 'Deselect all' : 'Select all'}
            </div>
          )}
        </div>
      ) : (
        <div className="pb-4 pt-1" />
      )}
      <div className="customNormalScroll overflow-y-auto px-3" style={{ maxHeight: window.innerHeight * 0.33 }}>
        {filteredData?.map((item, index) => {
          const itemId = String(item[dataIdKey]);
          const isSelected = selectedDraftArray.includes(itemId);
          const inputId = `default-${selectionType}-${itemId}-${index}`;
          const isRadio = selectionType === FilterSelectionType.RADIO;

          const renderRadioIcon = () => {
            if (isSelected) {
              return <SelectionCircleBlueIcon className="shrink-0" />;
            }
            return <SelectionCircleIcon className="shrink-0" />;
          };

          return (
            <React.Fragment key={`${String(item[dataKey])}--${itemId}-${index}`}>
              <div className="mb-4 flex items-center">
                <div
                  className="cursor-pointer"
                  onClick={e => {
                    e.stopPropagation();
                    handleChange(itemId);
                  }}
                  role="presentation">
                  {isRadio ? (
                    renderRadioIcon()
                  ) : (
                    <input
                      id={inputId}
                      type={selectionType}
                      value=""
                      onChange={e => {
                        e.stopPropagation();
                        handleChange(itemId);
                      }}
                      checked={isSelected}
                      className="h-4 w-4 cursor-pointer rounded pl-1 accent-primaryBtn"
                    />
                  )}
                </div>
                <label
                  htmlFor={isRadio ? undefined : inputId}
                  className="ml-4 cursor-pointer break-words text-sbase font-medium line-clamp-2"
                  onClick={e => {
                    if (isRadio) {
                      e.stopPropagation();
                      handleChange(itemId);
                    }
                  }}
                  role={isRadio ? 'button' : undefined}
                  tabIndex={isRadio ? 0 : undefined}
                  onKeyDown={e => {
                    if (isRadio && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleChange(itemId);
                    }
                  }}>
                  {showCustomOptions
                    ? customOption(item)
                    : `${item[dataKey]} ${secondaryDataKey ? item[secondaryDataKey] : ''}`}
                </label>
              </div>
              {showSeparatorAfterFirstItem && index === 0 && <Divider className="mb-3 -mt-1 border-borderGray" />}
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-2 flex w-full justify-end rounded-b-md bg-secondaryBtn px-4 py-2.5">
        <div onClick={close} className="cursor-pointer text-sbase font-semibold text-accentText">
          Cancel
        </div>
        <div
          id={`${filterId}-apply-button`}
          onClick={() => {
            applyFilter();
            close();
          }}
          className="ml-5 cursor-pointer text-sbase font-semibold text-secondary">
          Apply
        </div>
      </div>
    </>
  );
};

export default DropdownPopoverPanel;
